import React, { useState, useEffect } from 'react';
import { Link, graphql } from 'gatsby';
import { EyeIcon } from '@heroicons/react/outline';

import Layout from '../components/layout';
import LinkBox from '../components/link-box';
import EpisodeNav from '../components/episode-nav';
import SEO from '../components/seo';

import { durationToSeconds } from '../utils/duration-to-seconds';
import { formatNumber } from '../utils/format-number';
import { getVideoViews } from '../services/youtube-api';

export const query = graphql`
  query ($id: String!, $guest: String) {
    episodeDetails: episodesJson(id: { eq: $id }) {
      id
      guest
      tagline
      intro
      publishedAt(formatString: "ddd, MMM D, YYYY")
      videoId
      spotify
      duration
      categories
      timestamps
      booksMentioned {
        title
        author
        published(formatString: "MMM D, YYYY")
        wikipedia
        goodreads
        amazon
        imageUrl
        timestamp
      }
    }
    otherAppearances: allEpisodesJson(
      filter: { guest: { regex: $guest }, id: { ne: $id } }
    ) {
      edges {
        node {
          id
          guest
          fields {
            slug
          }
        }
      }
    }
  }
`;

const Episode = ({ pageContext, data, location }) => {
  const [showYoutube, setShowYoutube] = useState(!location.state?.hideVideo);
  const [loadingViews, setLoadingViews] = useState(true);
  const [views, setViews] = useState(null);

  const {
    id,
    guest,
    intro,
    tagline,
    videoId,
    spotify,
    categories,
    publishedAt,
    booksMentioned,
    timestamps,
  } = data.episodeDetails || '';
  const otherAppearances = data.otherAppearances.edges;

  useEffect(() => {
    const getViewCount = async () => {
      const viewCount = await getVideoViews(videoId);
      setViews(viewCount);
      setLoadingViews(false);
    };
    getViewCount();
  }, []);

  return (
    <Layout>
      <SEO title={`#${id} - ${guest.replace(' | ', ', ')}`} />
      <div>
        <h3>
          #{id} <br />
          {guest.replace(' | ', ', ')} - {tagline}
        </h3>
        <div className="mt-1 md:mt-2 mb-3 text-gray-400 text-lg">
          {publishedAt}
          {' · '}
          <div className="h-4 w-5 inline-block">
            <EyeIcon />
          </div>{' '}
          {loadingViews
            ? 'Loading views'
            : views instanceof Error
            ? `N/A`
            : `${formatNumber(views)} views`}
          {otherAppearances.length > 0 && (
            <div className="mt-1 md:mt-2 mb-3 text-gray-400 text-lg">
              Other appearances:{' '}
              {otherAppearances.map((edge) => (
                <div
                  className="inline mr-2 border-b border-gray-500"
                  key={`prev-${edge.node.id}`}
                >
                  <Link to={`/episodes/${edge.node.fields.slug}`}>
                    {edge.node.id}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-1 mb-1">{intro}</div>
        {booksMentioned && (
          <details
            className="mt-3 mb-3"
            open={location.state?.showBooks || null}
          >
            <summary>
              <h4 className="inline" id={`books`}>
                Books mentioned
              </h4>
            </summary>
            <div className="mt-2 mb-3 flex flex-row flex-wrap gap-2">
              {booksMentioned.map((book, idx) => {
                const {
                  title,
                  author,
                  published,
                  amazon,
                  imageUrl,
                  wikipedia,
                  goodreads,
                  timestamp,
                } = book;

                return (
                  <div
                    className="flex flex-col px-5 py-2"
                    key={`book-${id}-${idx}`}
                  >
                    <a href={amazon} target="_blank" rel="noopener noreferrer">
                      <img
                        key={idx}
                        src={imageUrl}
                        alt={title}
                        width="200"
                        className="rounded-sm mb-5"
                        loading="lazy"
                      />
                    </a>
                    <div className="flex flex-col w-52 max-w-xs gap-4">
                      <a
                        href={amazon}
                        className="text-gradient text-md"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {title}
                      </a>
                      <span className="-mt-3 text-sm">{author}</span>
                      {published && (
                        <span className="-mt-3 text-sm">
                          Published:{' '}
                          <span className="text-gray-400">{published}</span>{' '}
                        </span>
                      )}
                      {timestamp && (
                        <span className="-mt-3 text-sm">
                          Timestamp:{' '}
                          <a
                            href={`https://www.youtube.com/watch?v=${videoId}&t=${durationToSeconds(
                              timestamp
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gradient"
                          >
                            {timestamp}
                          </a>{' '}
                        </span>
                      )}
                      <div className="flex gap-2 -mt-3">
                        {wikipedia && (
                          <a
                            href={wikipedia}
                            className="text-sm border-b border-gray-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Wikipedia
                          </a>
                        )}
                        {goodreads && (
                          <a
                            href={goodreads}
                            className="text-sm border-b border-gray-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Goodreads
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}
        {timestamps && (
          <details className="mt-3 mb-3">
            <summary>
              <h4 className="inline">Timestamps</h4>
            </summary>
            <ul className="mb-1 ml-1 border-l border-gray-500 pl-4 list-none">
              {timestamps.map((timestamp, idx) => (
                <li key={`timestamp-${idx}`} className="leading-8 text-lg">
                  <a
                    href={`https://www.youtube.com/watch?v=${videoId}&t=${durationToSeconds(
                      timestamp.split(' - ')[0]
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-gradient"
                  >
                    {timestamp.split(' - ')[0]}
                  </a>{' '}
                  - {timestamp.split(' - ')[1]}
                </li>
              ))}
            </ul>
          </details>
        )}
        <details className="mt-2 mb-3">
          <summary>
            <h4 className="inline">Listen on Spotify</h4>
          </summary>
          <div className="mt-3 hidden md:block">
            <iframe
              src={`https://open.spotify.com/embed/episode/${spotify}?theme=0`}
              title="Spotify player"
              width="100%"
              height="232"
              frameBorder="0"
              allowFullScreen=""
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            ></iframe>
          </div>
          <div className="mt-3 md:hidden">
            <iframe
              src={`https://open.spotify.com/embed/episode/${spotify}?theme=0`}
              title="Spotify player"
              width="100%"
              height="152"
              frameBorder="0"
              allowFullScreen=""
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            ></iframe>
          </div>
        </details>
        <details
          className="mt-1 mb-3"
          open={location.state?.hideVideo ? null : true}
        >
          <summary
            onClick={() => setShowYoutube((showYoutube) => !showYoutube)}
            onKeyDown={() => setShowYoutube((showYoutube) => !showYoutube)}
          >
            <h4 className="inline" role="button" tabIndex="0">
              {showYoutube ? 'Hide video' : 'Show video'}
            </h4>
          </summary>
          <div className="aspect-w-16 aspect-h-9 mt-3">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen="1"
              loading="lazy"
            ></iframe>
          </div>
        </details>
        <div className="mt-10 mb-10 text-lg flex flex-wrap gap-2">
          {categories.map((category, idx) => (
            <LinkBox
              key={idx}
              href={`/categories/${category.replace(/^\w+_/, '')}`}
              variant="small"
            >
              #{category.replace(/^\w+_/, '')}
            </LinkBox>
          ))}
        </div>
      </div>
      <div>
        <EpisodeNav
          prev={pageContext.prevEpisode}
          next={pageContext.nextEpisode}
        />
      </div>
    </Layout>
  );
};

export default Episode;
